import { tracked } from "@glimmer/tracking";
import EmberObject from "@ember/object";
import { trackedArray } from "@ember/reactive/collections";
import { bind } from "discourse/lib/decorators";
import User from "discourse/models/user";
import ChatChannel from "discourse/plugins/chat/discourse/models/chat-channel" with {
  discourseImport: "optional",
};
import DiscoursePostEventEventStats from "./discourse-post-event-event-stats.js";
import DiscoursePostEventInvitee from "./discourse-post-event-invitee.js";

const DEFAULT_REMINDER = {
  type: "notification",
  value: 15,
  unit: "minutes",
  period: "before",
};
// Keep in sync with the constants of the same name on
// DiscoursePostEvent::Event, which enforces the same window server-side.
const EARLY_ACCESS_MINUTES = 30;
const GRACE_PERIOD_MINUTES = 10;

export function isPastEventTimeframe(allDay, startsAt, endsAt) {
  if (allDay) {
    if (!startsAt && !endsAt) {
      return false;
    }

    return moment().isAfter(moment(endsAt || startsAt).endOf("day"));
  }

  if (!endsAt) {
    return false;
  }

  return moment().isAfter(moment(endsAt).add(GRACE_PERIOD_MINUTES, "minutes"));
}

export function isWithinEventTimeframe(allDay, startsAt, endsAt) {
  if (!startsAt) {
    return false;
  }

  const now = moment();

  if (allDay) {
    const opensAt = moment(startsAt).startOf("day");
    const closesAt = moment(endsAt || startsAt).endOf("day");

    return now.isBetween(opensAt, closesAt);
  }

  const opensAt = moment(startsAt).subtract(EARLY_ACCESS_MINUTES, "minutes");

  // An event without an end time never closes, which is what the server does
  // when it issues a signature for one.
  if (!endsAt) {
    return now.isAfter(opensAt);
  }

  const closesAt = moment(endsAt).add(GRACE_PERIOD_MINUTES, "minutes");

  return now.isBetween(opensAt, closesAt);
}

export default class DiscoursePostEventEvent {
  static create(args = {}) {
    return new DiscoursePostEventEvent(args);
  }

  @tracked rrule;
  @tracked name;
  @tracked categoryId;
  @tracked startsAt;
  @tracked endsAt;
  @tracked allDay;
  @tracked duration;
  @tracked rawInvitees;
  @tracked location;
  @tracked locationHtml;
  @tracked urlRestatesLocation;
  @tracked url;
  @tracked description;
  @tracked descriptionHtml;
  @tracked timezone;
  @tracked showLocalTime;
  @tracked status;
  @tracked post;
  @tracked minimal;
  @tracked chatEnabled;
  @tracked livestream;
  @tracked livestreamOnebox;
  @tracked livestreamUrl;
  @tracked livestreamChatChannelId;
  @tracked isZoomLivestream;
  @tracked canUpdateAttendance;
  @tracked canActOnDiscoursePostEvent;
  @tracked shouldDisplayInvitees;
  @tracked isClosed;
  @tracked isExpired;
  @tracked isStandalone;
  @tracked maxAttendees;
  @tracked atCapacity;
  @tracked recurrenceUntil;
  @tracked recurrence;
  @tracked customFields;
  @tracked channel;
  @tracked imageUpload;
  @tracked isFutureOccurrence = false;

  @tracked _watchingInvitee;
  @tracked _sampleInvitees;
  @tracked _stats;
  @tracked _creator;
  @tracked _hosts;
  @tracked _reminders;

  constructor(args = {}) {
    this.id = args.id;
    this.rrule = args.rrule;
    this.name = args.name;
    this.categoryId = args.category_id;
    this.startsAt = args.starts_at;
    this.endsAt = args.ends_at;
    this.allDay = args.all_day || false;
    this.duration = args.duration;
    this.rawInvitees = args.raw_invitees;
    this.sampleInvitees = args.sample_invitees || [];
    this.location = args.location;
    this.locationHtml = args.location_html;
    this.urlRestatesLocation = args.url_restates_location;
    this.url = args.url;
    this.description = args.description;
    this.descriptionHtml = args.description_html;
    this.timezone = args.timezone;
    this.showLocalTime = args.show_local_time;
    this.status = args.status;
    this.creator = args.creator;
    this.hosts = args.hosts || [];
    this.post = args.post;
    this.isClosed = args.is_closed;
    this.isExpired = args.is_expired;
    this.isStandalone = args.is_standalone;
    this.minimal = args.minimal;
    this.chatEnabled = args.chat_enabled;
    this.livestream = args.livestream;
    this.livestreamOnebox = args.livestream_onebox;
    this.livestreamUrl = args.livestream_url;
    this.livestreamChatChannelId = args.livestream_chat_channel_id;
    this.isZoomLivestream = args.is_zoom_livestream;
    this.maxAttendees = args.max_attendees;
    this.atCapacity = args.at_capacity;
    this.recurrence = args.recurrence;
    this.recurrenceUntil = args.recurrence_until;
    this.canUpdateAttendance = args.can_update_attendance;
    this.canActOnDiscoursePostEvent = args.can_act_on_discourse_post_event;
    this.shouldDisplayInvitees = args.should_display_invitees;
    this.watchingInvitee = args.watching_invitee;
    this.stats = args.stats;
    this.reminders = args.reminders;
    this.customFields = EmberObject.create(args.custom_fields || {});
    if (args.channel && ChatChannel) {
      this.channel = ChatChannel.create(args.channel);
    }
    this.imageUpload = args.image_upload;
  }

  get watchingInvitee() {
    return this._watchingInvitee;
  }

  set watchingInvitee(invitee) {
    this._watchingInvitee = invitee
      ? DiscoursePostEventInvitee.create(invitee)
      : null;
  }

  get sampleInvitees() {
    return this._sampleInvitees;
  }

  set sampleInvitees(invitees = []) {
    this._sampleInvitees = trackedArray(
      invitees.map((i) => DiscoursePostEventInvitee.create(i))
    );
  }

  get stats() {
    return this._stats;
  }

  set stats(stats) {
    this._stats = this.#initStatsModel(stats);
  }

  get reminders() {
    return this._reminders;
  }

  set reminders(reminders = []) {
    this._reminders = trackedArray(reminders);
  }

  get creator() {
    return this._creator;
  }

  set creator(user) {
    this._creator = this.#initUserModel(user);
  }

  get hosts() {
    return this._hosts;
  }

  set hosts(users = []) {
    this._hosts = trackedArray(
      (users ?? []).map((user) => this.#initUserModel(user))
    );
  }

  get isPublic() {
    return this.status === "public";
  }

  get isPrivate() {
    return this.status === "private";
  }

  get currentlyWithinEventTimeframe() {
    return isWithinEventTimeframe(this.allDay, this.startsAt, this.endsAt);
  }

  get pastEventTimeframe() {
    return isPastEventTimeframe(this.allDay, this.startsAt, this.endsAt);
  }

  filterForFutureOccurrence() {
    this.isFutureOccurrence = true;
    this.sampleInvitees = this.sampleInvitees.filter(
      (invitee) => invitee.status !== "going" || invitee.recurring
    );

    const recurringCount = this.stats?.goingRecurring ?? 0;
    if (this.stats) {
      this.stats.going = recurringCount;
    }
    this.atCapacity =
      this.maxAttendees != null && recurringCount >= this.maxAttendees;

    if (
      this.watchingInvitee?.status === "going" &&
      !this.watchingInvitee?.recurring
    ) {
      this.watchingInvitee = null;
    }
  }

  updateFromEvent(event) {
    this.name = event.name;
    this.startsAt = event.startsAt;
    this.endsAt = event.endsAt;
    this.allDay = event.allDay;
    this.duration = event.duration;
    this.location = event.location;
    this.locationHtml = event.locationHtml;
    this.urlRestatesLocation = event.urlRestatesLocation;
    this.url = event.url;
    this.timezone = event.timezone;
    this.showLocalTime = event.showLocalTime;
    this.description = event.description;
    this.descriptionHtml = event.descriptionHtml;
    this.status = event.status;
    this.creator = event.creator;
    this.hosts = event.hosts || [];
    this.isClosed = event.isClosed;
    this.isExpired = event.isExpired;
    this.isStandalone = event.isStandalone;
    this.minimal = event.minimal;
    this.chatEnabled = event.chatEnabled;
    this.livestream = event.livestream;
    this.livestreamOnebox = event.livestreamOnebox;
    this.livestreamUrl = this.location || this.url;
    this.livestreamChatChannelId = event.livestreamChatChannelId;
    this.isZoomLivestream = event.isZoomLivestream;
    this.rrule = event.rrule;
    this.maxAttendees = event.maxAttendees;
    this.atCapacity = event.atCapacity;
    this.recurrence = event.recurrence;
    this.recurrenceUntil = event.recurrenceUntil;
    this.canUpdateAttendance = event.canUpdateAttendance;
    this.canActOnDiscoursePostEvent = event.canActOnDiscoursePostEvent;
    this.shouldDisplayInvitees = event.shouldDisplayInvitees;
    this.stats = event.stats;
    this.watchingInvitee = event.watchingInvitee;
    this.sampleInvitees = event.sampleInvitees || [];
    this.reminders = event.reminders;
    this.imageUpload = event.imageUpload;
  }

  @bind
  removeReminder(reminder) {
    const index = this.reminders.findIndex((r) => r.id === reminder.id);
    if (index > -1) {
      this.reminders.splice(index, 1);
    }
  }

  @bind
  addReminder(reminder) {
    reminder ??= { ...DEFAULT_REMINDER };
    this.reminders.push(reminder);
  }

  #initUserModel(user) {
    if (!user || user instanceof User) {
      return user;
    }

    return User.create(user);
  }

  #initStatsModel(stats) {
    if (!stats || stats instanceof DiscoursePostEventEventStats) {
      return stats;
    }

    return DiscoursePostEventEventStats.create(stats);
  }
}
